import React from 'react';
import { Carousel } from 'antd';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import { useSimpleDeviceDetection } from '../hooks/useDeviceDetection';

// Interface สำหรับ SEO Content Data
interface SEOContentData {
  status: number;
  count: number;
  data: Array<{
    id: number;
    seo_content_type: string;
    action_ID?: number;
    category_ID?: number;
    location_type_ID?: number;
    location_ID?: number;
    branch_ID?: number;
    PROVINCE_ID?: number;
    AMPHUR_ID?: number;
    content?: string;
    content_status: number;
    paragraph?: Array<{
      title: string;
      detail: string;
    }>;
  }>;
}

interface HeaderCarouselProps {
  images: string[];
  mainTitle?: string;
  subTitle?: string;
  seoContentData?: SEOContentData;
  isMobile?: boolean;
  isTablet?: boolean;
}

const HeaderCarousel: React.FC<HeaderCarouselProps> = ({
  images = [],
  mainTitle,
  subTitle,
  seoContentData,
  isMobile: propIsMobile,
  isTablet: propIsTablet
}) => {
  const { t } = useTranslation('common');
  const { isMobile: detectedIsMobile, isTablet: detectedIsTablet } = useSimpleDeviceDetection();

  console.log("seoContentData",seoContentData);
  
  // Use props if provided, otherwise use detection
  const isMobile = propIsMobile !== undefined ? propIsMobile : detectedIsMobile;
  const isTablet = propIsTablet !== undefined ? propIsTablet : detectedIsTablet;
  
  // ฟังก์ชันสำหรับแปลง HTML content ให้เป็น plain text
  const stripHTMLTags = (html: string) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  // ฟังก์ชันสำหรับดึงข้อความสำหรับ subTitle จาก SEO content
  const getSEOSubTitle = () => {
    if (!seoContentData || !seoContentData.data || seoContentData.data.length === 0) {
      return null;
    }

    const seoData = seoContentData.data[0];
    
    // ใช้เฉพาะ content
    if (seoData.content) {
      const cleanText = stripHTMLTags(seoData.content);
      return cleanText.length > 200 ? cleanText.substring(0, 280) + '...' : cleanText;
    }
    
    return null;
  };

  // ใช้ค่าจาก i18n หรือใช้ค่า default ถ้าไม่ได้กำหนด
  const displayMainTitle = mainTitle || t('headerCarousel.mainTitle');
  
  // ลองใช้ข้อมูลจาก SEO content ก่อน ถ้าไม่มีจึงใช้ fallback
  const seoSubTitle = getSEOSubTitle();
  const displaySubTitle = seoSubTitle || subTitle || t('headerCarousel.subTitle');

  // แปลงข้อความที่มี \n เป็น <br/>
  const formattedMainTitle = displayMainTitle.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < displayMainTitle.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  const formattedSubTitle = displaySubTitle.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < displaySubTitle.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  // Responsive styles based on Figma design
  // Mobile: fontSize 23px main, 16px sub, gap 8px
  // Tablet: fontSize 40px main, 16px sub, gap 24px  
  // Desktop: fontSize 57px main, 19px sub
  const getResponsiveStyles = () => {
    if (isMobile) {
      return {
        mainTitle: {
          fontSize: '23px',
          fontWeight: '500',
          lineHeight: '130%', // 1.3 from Figma
        },
        subTitle: {
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '125%', // 1.25 from Figma
          marginTop: '8px', // gap from Figma
        },
        container: {
          width: '90%',
          maxWidth: '320px',
        },
        carousel: {
          height: '400px', // Smaller height for mobile
        }
      };
    } else if (isTablet) {
      return {
        mainTitle: {
          fontSize: '40px',
          fontWeight: '500',
          lineHeight: '125%', // 1.25 from Figma
        },
        subTitle: {
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '125%', // 1.25 from Figma
          marginTop: '12px', // gap from Figma
        },
        container: {
          width: '85%',
          maxWidth: '650px',
        },
        carousel: {
          height: '500px', // Medium height for tablet
        }
      };
    } else {
      return {
        mainTitle: {
          fontSize: '57px',
          fontWeight: '500',
          lineHeight: '120%',
        },
        subTitle: {
          fontSize: '19px',
          fontWeight: '400',
          lineHeight: '125%',
          marginTop: '16px',
        },
        container: {
          width: '80%',
          maxWidth: '1496px',
        },
        carousel: {
          height: '600px', // Full height for desktop
        }
      };
    }
  };

  const styles = getResponsiveStyles();

  return (
    <div style={{ position: 'relative' }}>
      <Carousel 
        autoplay 
        draggable 
        dots={false} 
        arrows={false}
        autoplaySpeed={10000}
        lazyLoad="progressive"
        style={{ marginTop: isMobile ? '-80px' : isTablet ? '-100px' : '-120px' }}
      >
        {images.map((imagePath, index) => (
          <div key={index}>
            <div style={{ height: styles.carousel.height, position: 'relative', width: '100%' }}>
              <Image
                src={imagePath}
                alt={t('headerCarousel.imageAlt', { index: index + 1 })}
                fill
                priority={index === 0}
                sizes="100vw"
                style={{
                  objectFit: 'cover',
                  objectPosition: isMobile ? 'center 60%' : isTablet ? 'center 50%' : 'center 45%'
                }}
              />
            </div>
          </div>
        ))}
      </Carousel>
      
      {/* ข้อความทับบน Carousel */}
      <div 
        style={{
          position: 'absolute',
          top: isMobile ? '65%' : '55%',
          left: isMobile ? '45%' : '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          textAlign: 'left',
          width: styles.container.width,
          maxWidth: styles.container.maxWidth,
        }}
      >
        <h1 
          style={{
            color: 'white',
            fontSize: styles.mainTitle.fontSize,
            fontWeight: styles.mainTitle.fontWeight,
            margin: 0,
            lineHeight: styles.mainTitle.lineHeight,
            fontFamily: 'Prompt, Inter, Noto Serif SC, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
          }}
        >
          {formattedMainTitle}
        </h1>
        <p 
          style={{
            color: 'white',
            fontSize: styles.subTitle.fontSize,
            fontWeight: styles.subTitle.fontWeight,
            margin: 0,
            marginTop: styles.subTitle.marginTop,
            lineHeight: styles.subTitle.lineHeight,
            fontFamily: 'Prompt, Inter, Noto Serif SC, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
          }}
        >
          {formattedSubTitle}
        </p>
      </div>
    </div>
  );
};

export default HeaderCarousel;